import { BaseComponent } from '../base-component.js';
import { session } from '../../client/session.js';
import { router } from '../../shared/router.js';
import { escapeHtml, escapeAttribute, escapeHtmlWithLineBreaks } from '../../shared/utils.js';
import { api } from '../../client/api.js';
import { theme } from '../../client/theme.js';
import { getIcon } from '../../shared/icons.js';
import type { Notification } from '../../shared/types.js';
import componentStyles from './dashboard.css?inline';

export class Dashboard extends BaseComponent {
  private activeTab = 'links';
  private mobileMenuOpen = false;
  private notifications: Notification[] = [];
  private onHashChange: (() => void) | null = null;
  private sessionUnsub: (() => void) | null = null;
  private menuItems = [
    { id: 'links', label: 'My Links', adminOnly: false, active: true, tier: 'all', icon: 'link' },
    { id: 'page', label: 'Page Design', adminOnly: false, active: true, tier: 'all', icon: 'file-pen' },
    { id: 'qr', label: 'QR Code', adminOnly: false, active: true, tier: 'all', icon: 'qrcode' },
    { id: 'analytics', label: 'Analytics', adminOnly: false, active: true, tier: 'all', icon: 'chart-pie' },
    { id: 'settings', label: 'Settings', adminOnly: false, active: true, tier: 'all', icon: 'sliders' },
    { id: 'logs', label: 'Logs', adminOnly: true, active: true, tier: 'all', icon: 'table-list' },
    { id: 'notifications', label: 'Notifications', adminOnly: true, active: true, tier: 'all', icon: 'bell' },
  ];

  connectedCallback(): void {
    this.activeTab = this.getTabFromHash();
    super.connectedCallback();
    this.sessionUnsub = session.subscribe(() => this.onSessionChange());
  }

  disconnectedCallback(): void {
    if (this.onHashChange) {
      window.removeEventListener('hashchange', this.onHashChange);
      this.onHashChange = null;
    }
    this.sessionUnsub?.();
    this.sessionUnsub = null;
  }

  private onSessionChange(): void {
    const user = session.getUser();
    const userNameEl = this.$('.user-name');
    if (userNameEl && user) {
      userNameEl.textContent = user.name;
    }
    this.updateNotificationBadge();
  }

  private getTabFromHash(): string {
    const hash = window.location.hash.slice(1);
    const validIds = this.menuItems.map((m) => m.id);
    return validIds.includes(hash) ? hash : 'links';
  }

  private updateActiveTab(tab: string): void {
    this.activeTab = tab;
    this.mobileMenuOpen = false;

    const content = this.$('#content');
    if (content) content.innerHTML = this.renderTabContent();

    this.$all('.nav-link').forEach((link) => {
      link.classList.toggle('active', link.getAttribute('data-tab') === tab);
    });

    this.$('.sidebar')?.classList.remove('open');
  }

  protected styles(): string {
    return componentStyles;
  }

  private renderTabContent(): string {
    switch (this.activeTab) {
      case 'links':
        return '<link-manager></link-manager>';
      case 'analytics':
        return '<analytics-dashboard></analytics-dashboard>';
      case 'page':
        return '<page-editor></page-editor>';
      case 'qr':
        return '<qr-generator></qr-generator>';
      case 'settings':
        return '<settings-panel></settings-panel>';
      case 'logs':
        return '<admin-logs></admin-logs>';
      case 'notifications':
        return '<admin-notifications></admin-notifications>';
      default:
        return '';
    }
  }

  private renderNavLinks(): string {
    const links = this.menuItems.map((item) => {
      if (!item.active) return '';
      if (item.adminOnly && !session.getUser()?.isAdmin) return '';
      return `<li class="nav-item">
        <a class="nav-link ${this.activeTab === item.id ? 'active' : ''}" data-tab="${item.id}" data-icon="${item.icon}">
          ${item.label}
        </a>
      </li>`
    });
    return links.join('');
  }

  private async loadNavIcons(): Promise<void> {
    const navLinks = this.$all('.nav-link[data-icon]');
    for (const link of navLinks) {
      const iconName = link.getAttribute('data-icon');
      if (iconName) {
        const iconSpan = document.createElement('span');
        iconSpan.setAttribute('class', 'icon');
        if (iconSpan) {
          const svgContent = await getIcon(iconName);
          iconSpan.innerHTML = svgContent;
          link.prepend(iconSpan);
        }
      }
    }
  }

  private async loadProfileIcon(): Promise<void> {
    const profileBtn = this.$('#profileBtn');
    if (profileBtn) {
      const iconSpan = profileBtn.querySelector('.icon');
      if (iconSpan) {
        const svgContent = await getIcon('circle-user');
        iconSpan.innerHTML = svgContent;
      }
    }
  }

  private async loadNotificationIcons(): Promise<void> {
    const bellIcon = this.$('.notifications-icon .icon');
    if (bellIcon) {
      const svgContent = await getIcon('bell');
      bellIcon.innerHTML = svgContent;
    }

    // Load close icon
    const closeIcon = this.$('.icon-btn .icon');
    if (closeIcon) {
      const svgContent = await getIcon('angles-right');
      closeIcon.innerHTML = svgContent;
    }
  }

  private notificationView(): string {
    return `
      <span class="notifications-icon" id="notificationsIcon" title="Notifications">
        <span class="icon"></span>
        <span id="notificationPip" class="notification-pip hidden"></span>
      </span>
      
      <div class="notifications-sidebar" id="notificationsSidebar">
        <div class="header">
          <h3>Notifications</h3>
          <button class="icon-btn" id="closeNotifications" aria-label="Close notifications">
            <span class="icon icon-xl"></span>
          </button>
        </div>
        <ul class="notifications-list" id="notificationsList">
          <li class="greyed-out">No new notifications.</li>
        </ul>
      </div>
    `;
  }

  private async loadNotifications(): Promise<void> {
    try {
      const response = await api.getNotifications();
      this.notifications = response.data || [];
      this.renderNotificationsList();
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }

  private renderNotificationsList(): void {
    const list = this.$('#notificationsList');
    if (!list) return;

    if (this.notifications.length === 0) {
      list.innerHTML = '<li class="greyed-out">No new notifications.</li>';
      return;
    }

    list.innerHTML = this.notifications
      .map(
        (n) => `
        <li data-notification-id="${escapeAttribute(n.id)}" class="${n.read ? 'read' : 'unread'}">
          <div class="notification-content">
            <div class="notification-title">${escapeHtml(n.title)}</div>
            <div class="notification-message">${escapeHtmlWithLineBreaks(n.message)}</div>
            ${n.actionUrl ? `<a href="${escapeAttribute(n.actionUrl)}" class="btn btn-primary notification-action">View</a>` : ''}
          </div>
          <button class="notification-delete" data-notification-id="${escapeAttribute(n.id)}" aria-label="Delete notification">&times;</button>
        </li>
      `
      )
      .join('');

    const items = list.querySelectorAll('li[data-notification-id]');
    items.forEach((item) => {
      item.addEventListener('click', async () => {
        const notificationId = (item as HTMLElement).dataset.notificationId;
        if (notificationId) {
          await this.markNotificationAsRead(notificationId);
        }
      });
    });

    const deleteButtons = list.querySelectorAll('.notification-delete');
    deleteButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        e.stopPropagation();
        const notificationId = (button as HTMLElement).dataset.notificationId;
        if (notificationId) {
          await this.deleteNotification(notificationId);
        }
      });
    });
  }

  private async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const result = await api.markNotificationAsRead(notificationId);
      const notification = this.notifications.find((n) => n.id === notificationId);
      if (notification) {
        notification.read = true;
      }
      this.renderNotificationsList();

      if (result.success && result.data?.hasUnreadNotifications !== undefined) {
        const user = session.getUser();
        if (user) {
          session.setUser({ ...user, hasUnreadNotifications: result.data.hasUnreadNotifications });
        }
      }
      this.updateNotificationBadge();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }

  private async deleteNotification(notificationId: string): Promise<void> {
    try {
      const result = await api.deleteNotification(notificationId);
      this.notifications = this.notifications.filter((n) => n.id !== notificationId);
      this.renderNotificationsList();

      if (result.success && result.data?.hasUnreadNotifications !== undefined) {
        const user = session.getUser();
        if (user) {
          session.setUser({ ...user, hasUnreadNotifications: result.data.hasUnreadNotifications });
        }
      }
      this.updateNotificationBadge();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }

  private async showNotificationsList(): Promise<void> {
    const sidebar = this.$('#notificationsSidebar');
    if (sidebar) {
      sidebar.classList.add('open');
      await this.loadNotifications();
    }
  }

  private hideNotificationsList(): void {
    const sidebar = this.$('#notificationsSidebar');
    if (sidebar) {
      sidebar.classList.remove('open');
    }
  }

  private hasNotifications(): boolean {
    const user = session.getUser();
    return user?.hasUnreadNotifications ?? false;
  }

  private updateNotificationBadge(): void {
    const pip = this.$('#notificationPip');
    if (pip) {
      if (this.hasNotifications()) {
        pip.classList.remove('hidden');
      } else {
        pip.classList.add('hidden');
      }
    }
  }

  protected template(): string {
    const user = session.getUser();
    const baseUrl = window.location.origin;
    const profileUrl = user ? `${baseUrl}/u/${encodeURIComponent(user.slug)}` : '';

    return `
      <div class="header">
        <div class="logo">Agentor</div>
        <div class="user-menu">
          ${user ? `
            ${this.notificationView()}
            <span class="user-name">${escapeHtml(user.name)}</span>
            <a href="${escapeAttribute(profileUrl)}" target="_blank" class="profile-btn" id="profileBtn" title="View My Page">
              <span class="icon"></span>
              <span class="profile-text">View My Page</span>
            </a>
          ` : ''}
          <button class="theme-toggle-btn" id="themeToggle" aria-label="Toggle theme" title="Toggle dark mode">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="theme-icon"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M361.5 1.2c5 2.1 8.6 6.6 9.6 11.9L391 121l107.9 19.8c5.3 1 9.8 4.6 11.9 9.6s1.5 10.7-1.6 15.2L446.9 256l62.3 90.3c3.1 4.5 3.7 10.2 1.6 15.2s-6.6 8.6-11.9 9.6L391 391 371.1 498.9c-1 5.3-4.6 9.8-9.6 11.9s-10.7 1.5-15.2-1.6L256 446.9l-90.3 62.3c-4.5 3.1-10.2 3.7-15.2 1.6s-8.6-6.6-9.6-11.9L121 391 13.1 371.1c-5.3-1-9.8-4.6-11.9-9.6s-1.5-10.7 1.6-15.2L65.1 256 2.8 165.7c-3.1-4.5-3.7-10.2-1.6-15.2s6.6-8.6 11.9-9.6L121 121 140.9 13.1c1-5.3 4.6-9.8 9.6-11.9s10.7-1.5 15.2 1.6L256 65.1 346.3 2.8c4.5-3.1 10.2-3.7 15.2-1.6zM160 256a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zm224 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0z"/></svg>
          </button>
          <button class="logout-btn" id="logoutBtn">Log Out</button>
        </div>
      </div>
      
      <button class="mobile-menu-toggle" id="menuToggle" aria-label="Toggle menu">
        <span class="menu-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z"/></svg>
        </span>
      </button>
      
      <div class="main">
        <aside class="sidebar ${this.mobileMenuOpen ? 'open' : ''}">
          <ul class="nav">
           ${this.renderNavLinks()}
          </ul>
        </aside>
        
        <div class="content" id="content">
          ${this.renderTabContent()}
        </div>
      </div>
    `;
  }

  protected afterRender(): void {
    const logoutBtn = this.$('#logoutBtn');
    const themeToggle = this.$('#themeToggle');
    const menuToggle = this.$('#menuToggle');
    const sidebar = this.$('.sidebar');
    const navLinks = this.$all('.nav-link');
    const notificationsSidebar = this.$('#notificationsSidebar');
    const notificationsIcon = this.$('#notificationsIcon');
    const notificationPip = this.$('#notificationPip');
    const closeNotifications = this.$('#closeNotifications');

    this.loadNavIcons();
    this.loadProfileIcon();
    this.loadNotificationIcons();

    this.updateNotificationBadge();

    themeToggle?.addEventListener('click', () => {
      theme.toggle();
    });

    notificationsIcon?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (notificationsSidebar?.classList.contains('open')) {
        this.hideNotificationsList();
      } else {
        this.showNotificationsList();
        notificationPip?.classList.add('hidden');
      }
    });

    closeNotifications?.addEventListener('click', () => {
      this.hideNotificationsList();
    });

    logoutBtn?.addEventListener('click', async () => {
      await session.logout();
      router.navigate('/');
    });

    menuToggle?.addEventListener('click', () => {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      if (sidebar) {
        sidebar.classList.toggle('open', this.mobileMenuOpen);
      }
    });

    navLinks.forEach((link) => {
      link.addEventListener('click', () => {
        const tab = link.getAttribute('data-tab');
        if (tab) {
          location.hash = tab;
        }
      });
    });

    if (!this.onHashChange) {
      this.onHashChange = () => this.updateActiveTab(this.getTabFromHash());
      window.addEventListener('hashchange', this.onHashChange);
    }
  }
}

customElements.define('app-dashboard', Dashboard);
